import { getParsedUser } from './authStorage';

import { sanitizeUserFacingError } from './userFacingError';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || '';

/** Primary + backup mounts (see raheasy26backend routes/products|analytics|sessions). */
const FEEDBACK_API_BASES = [
  '/api/feedback/platform',
  '/api/products/feedback/platform',
  '/api/analytics/feedback/platform',
  '/api/sessions/feedback/platform'
];

async function feedbackFetch(pathAndQuery = '', options = {}) {
  if (!API_BASE) throw new Error('API not configured');
  let lastRes = null;
  for (const base of FEEDBACK_API_BASES) {
    const url = `${API_BASE}${base}${pathAndQuery}`;
    const res = await fetch(url, options);
    if (res.status !== 404) return res;
    lastRes = res;
  }
  return lastRes;
}
const COOLDOWN_KEY = 'raheasy_platform_feedback_at';

/** Shown when the feedback API is unreachable (e.g. not yet deployed). */
export const FEEDBACK_UNAVAILABLE_MESSAGE =
  "We couldn't save your feedback right now. Please try again in a few minutes, or contact support if this continues.";

const SESSION_KEY = 'raheasy_session_id';
const PENDING_PROMPT_KEY = 'raheasy_feedback_pending_source';
const PENDING_SCORE_KEY = 'raheasy_feedback_pending_score';

export const FEEDBACK_TAGS = [
  { id: 'browsing', label: 'Browsing' },
  { id: 'finding_products', label: 'Finding products' },
  { id: 'support', label: 'Support' },
  { id: 'checkout', label: 'Checkout' }
];

export function getFeedbackSessionId() {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

export function markLocalFeedbackSubmitted() {
  localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
}

export function isLocalFeedbackCooldownActive() {
  const raw = localStorage.getItem(COOLDOWN_KEY);
  if (!raw) return false;
  const at = parseInt(raw, 10);
  if (!Number.isFinite(at)) return false;
  const ninetyDays = 90 * 24 * 60 * 60 * 1000;
  return Date.now() - at < ninetyDays;
}

export function queueFeedbackPrompt(source, initialScore) {
  sessionStorage.setItem(PENDING_PROMPT_KEY, source);
  if (initialScore >= 1 && initialScore <= 5) {
    sessionStorage.setItem(PENDING_SCORE_KEY, String(initialScore));
  }
  window.dispatchEvent(
    new CustomEvent('raheasy:feedback-prompt', {
      detail: { source, initialScore: initialScore >= 1 && initialScore <= 5 ? initialScore : undefined }
    })
  );
}

/** Open the feedback modal immediately (e.g. footer star click). Optional 1–5 pre-selected stars. */
export function openFeedbackPrompt(source = 'footer', initialScore) {
  const score =
    initialScore >= 1 && initialScore <= 5 ? initialScore : undefined;
  window.dispatchEvent(
    new CustomEvent('raheasy:feedback-prompt', {
      detail: { source, initialScore: score, force: true },
    })
  );
}

export function consumePendingFeedbackSource() {
  const s = sessionStorage.getItem(PENDING_PROMPT_KEY);
  if (s) sessionStorage.removeItem(PENDING_PROMPT_KEY);
  return s;
}

export function consumePendingFeedbackScore() {
  const raw = sessionStorage.getItem(PENDING_SCORE_KEY);
  sessionStorage.removeItem(PENDING_SCORE_KEY);
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : 0;
}

export function openWhatsAppOrder(url) {
  sessionStorage.setItem(PENDING_PROMPT_KEY, 'post_order');
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function fetchFeedbackSummary() {
  if (!API_BASE) return null;
  try {
    const res = await feedbackFetch('/summary');
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function fetchFeedbackExperiences({ page = 1, limit = 50 } = {}) {
  if (!API_BASE) return { items: [], total: 0, page: 1, pages: 1 };
  try {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const res = await feedbackFetch(`/experiences?${params.toString()}`);
    if (!res.ok) return { items: [], total: 0, page: 1, pages: 1 };
    return res.json();
  } catch {
    return { items: [], total: 0, page: 1, pages: 1 };
  }
}

export function openFeedbackExperiences() {
  window.dispatchEvent(new CustomEvent('raheasy:feedback-experiences-open'));
}

export async function checkFeedbackEligible() {
  if (isLocalFeedbackCooldownActive()) {
    return { canSubmit: false, reason: 'You already shared feedback recently. Thank you!' };
  }
  if (!API_BASE) return { canSubmit: true };
  try {
    const sessionId = getFeedbackSessionId();
    const res = await feedbackFetch(
      `/eligible?sessionId=${encodeURIComponent(sessionId)}`
    );
    if (!res.ok) return { canSubmit: true };
    return res.json();
  } catch {
    return { canSubmit: true };
  }
}

export async function submitPlatformFeedback({ score, comment, tags, source, page }) {
  if (!API_BASE) throw new Error('API not configured');
  const sessionId = getFeedbackSessionId();
  const user = getParsedUser();
  const headers = { 'Content-Type': 'application/json' };
  if (user?.token) headers.Authorization = `Bearer ${user.token}`;

  const res = await feedbackFetch('', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      score: Math.round(Number(score)),
      comment: comment || '',
      tags: tags || [],
      source,
      page: page || window.location.pathname,
      sessionId
    })
  });
  const raw = await res.text();
  let data = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    /* non-JSON body (e.g. HTML 404 page) */
  }
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error(FEEDBACK_UNAVAILABLE_MESSAGE);
    }
    const msg =
      data.message ||
      (typeof data.error === 'string' ? data.error : '') ||
      (raw && raw.length < 200 && !raw.startsWith('<') ? raw : '');
    throw new Error(sanitizeUserFacingError(msg, `Could not submit feedback (${res.status})`));
  }
  markLocalFeedbackSubmitted();
  return data;
}
